// slices/chat/addCitationBlock.ts
import type { Dispatch } from 'redux';
import { addChatConversationBlock } from '../../services/chatConversations';
import type { ChatRootState } from './chatSelectors';
import { blockMessageAdded } from './chatSlice';

export interface AddCitationBlockArgs {
  /** Client id of the answer whose source is being added. */
  messageId: string;
  citationId: string;
}

export type AddCitationBlockResult =
  { ok: true; messageId: string } | { ok: false };

/**
 * Add one of an answer's sources to the conversation as a block (ORA-514).
 *
 * No model turn runs: the server builds the block from the cited content,
 * appends a block-only message after the answer, and records the item as
 * served so the next turn's history sees it. The wire names the SERVER's
 * message id — a just-streamed answer the server has not named yet has no
 * sources to add, and the pill does not offer it.
 *
 * Resolves rather than throws, so the pill can re-enable itself and say what
 * happened without the thread caring why.
 */
export const addCitationBlock =
  ({ messageId, citationId }: AddCitationBlockArgs) =>
  async (
    dispatch: Dispatch,
    getState: () => ChatRootState
  ): Promise<AddCitationBlockResult> => {
    const message = getState().chat.messages[messageId];
    if (!message?.serverId) return { ok: false };

    const { conversationId } = message;

    try {
      const added = await addChatConversationBlock(conversationId, {
        messageId: message.serverId,
        citationId,
      });

      dispatch(
        blockMessageAdded({
          conversationId,
          afterMessageId: messageId,
          // A block-only message carries no attachments; the rest is already
          // in the read shape the slice hydrates from.
          message: {
            id: added.id,
            role: added.role,
            text: added.text,
            citations: added.citations,
            blocks: added.blocks,
          },
        })
      );

      return { ok: true, messageId: added.id };
    } catch (caught) {
      console.error('Unable to add a chat source as a block', caught);
      return { ok: false };
    }
  };
